import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Avatar,
  Box,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Person as PersonIcon,
  Home as TenantIcon,
  Business as PropertyManagerIcon,
  AccountBalance as CommonAccountIcon,
} from "@mui/icons-material";
import StatusTag from "modules/apl/components/StatusTag";
import { setSnackbar } from "modules/snackbar/api/snackbar";
import { formatAccountCalendarDate } from "../utils/formatAccountCalendarDate";

function computeTenure(moveInDate, moveOutDate) {
  const start = new Date(moveInDate);
  const end = moveOutDate ? new Date(moveOutDate) : new Date();
  const diffTime = Math.abs(end - start);
  const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));
  const diffMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30)) % 12;

  if (diffYears > 0) {
    let duration = `${diffYears} year${diffYears > 1 ? "s" : ""}`;
    if (diffMonths > 0) duration += `, ${diffMonths} month${diffMonths > 1 ? "s" : ""}`;
    return duration;
  }
  if (diffMonths > 0) return `${diffMonths} month${diffMonths > 1 ? "s" : ""}`;
  return "Less than a month";
}

const AccountHeader = React.memo(function AccountHeader() {
  const dispatch = useDispatch();
  const account = useSelector((state) => state.account);
  const apl = useSelector((state) => state.apl);
  const complaints = useSelector((state) => state.complaints);
  const hasOpenComplaints = complaints?.accountComplaints?.items?.some(
    (c) => c.complaintStatus !== "COMPLETED"
  );

  const acctData = account.data.account;
  const acctStatusId = acctData ? acctData.accountStatusId : -1;
  const customerTypeId = acctData ? acctData.customerTypeId : -1;

  const avatarContent = useMemo(() => {
    if (account.isLoading || !acctData) return null;
    const customerType = apl.data.customerTypes?.find((t) => t.id === customerTypeId);
    if (customerType) {
      const name = customerType.description?.toLowerCase();
      if (name?.includes("tenant"))
        return { icon: <TenantIcon sx={{ fontSize: 32 }} />, bgcolor: "primary.main" };
      if (name?.includes("property") || name?.includes("manager"))
        return { icon: <PropertyManagerIcon sx={{ fontSize: 32 }} />, bgcolor: "secondary.main" };
      return { icon: <CommonAccountIcon sx={{ fontSize: 32 }} />, bgcolor: "info.main" };
    }
    return { icon: <PersonIcon sx={{ fontSize: 32 }} />, bgcolor: "grey.500" };
  }, [account.isLoading, acctData, apl.data.customerTypes, customerTypeId]);

  const moveInOutDates = useMemo(() => {
    if (!acctData) return null;
    const accountStatus = apl?.genericTypes?.accountStatus?.data?.find(
      (s) => s.id === acctData.accountStatusId
    );
    const isInactive = accountStatus?.description?.toLowerCase() === "inactive";

    const hasMoveIn =
      acctData.moveInDateOverride ||
      (account.data.move?.moveIn && account.data.move.moveIn[0]?.moveInDate) ||
      acctData.moveInDate;
    const hasMoveOut =
      acctData.moveOutDateOverride ||
      (account.data.move?.moveOut && account.data.move.moveOut?.moveOutDate) ||
      acctData.moveOutDate;
    const isCurrentOccupant = hasMoveIn && !hasMoveOut;

    if (!isInactive && !isCurrentOccupant) return null;

    const getMoveDate = (override, moveData, fallback) => {
      if (override) return new Date(override);
      if (moveData) return new Date(moveData);
      if (fallback) return new Date(fallback);
      return null;
    };

    const moveIn = getMoveDate(
      acctData.moveInDateOverride,
      account.data.move?.moveIn?.[0]?.moveInDate,
      acctData.moveInDate
    );
    const moveOut = getMoveDate(
      acctData.moveOutDateOverride,
      account.data.move?.moveOut?.moveOutDate,
      acctData.moveOutDate
    );

    return { moveIn, moveOut };
  }, [acctData, apl?.genericTypes?.accountStatus?.data, account.data.move]);

  const unitAddress = useMemo(() => {
    const unit = account.data.unit;
    const property = account.data.property;
    if (!unit && !property) return null;

    let address = "";
    if (unit?.unitNum) address += `Unit ${unit.unitNum}`;

    if (unit && (unit.address1 || unit.address2)) {
      if (address && (unit.address1 || unit.address2)) address += ", ";
      if (unit.address1) address += unit.address1;
      if (unit.address2) address += (address ? ", " : "") + unit.address2;
      if (unit.city) address += (address ? ", " : "") + unit.city;
      if (unit.state) address += (address ? ", " : "") + unit.state;
      if (unit.zip) address += (address ? " " : "") + unit.zip;
    } else if (property) {
      const streetAddress = property.streetAddress || property.address || property.street || property.streetName;
      const { city, zip: pZip, zipCode, postalCode } = property;
      const state = property.state || property.stateCode;
      const zip = pZip || zipCode || postalCode;
      if (address && streetAddress) address += ", ";
      if (streetAddress) address += streetAddress;
      if (city) address += (address ? ", " : "") + city;
      if (state) address += (address ? ", " : "") + state;
      if (zip) address += (address ? " " : "") + zip;
    }

    return address || "Unit Address";
  }, [account.data.unit, account.data.property]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text.toString());
    dispatch(setSnackbar(true, "success", `${label} copied to clipboard`));
  };

  const clickableTextSx = {
    opacity: 0.9,
    cursor: "pointer",
    "&:hover": { opacity: 1, textDecoration: "underline" },
  };

  const accountLoading = account.isLoading;
  const aplAccountStatus = apl?.genericTypes?.accountStatus;
  const aplCustomerType = apl?.genericTypes?.customerType;
  const badgesReady =
    !accountLoading &&
    acctData &&
    aplAccountStatus?.data?.length > 0 &&
    aplCustomerType?.data?.length > 0;
  const isLoading = accountLoading || !badgesReady;

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minHeight: 120 }}>
      {isLoading ? (
        <Skeleton variant="circular" width={64} height={64} sx={{ flexShrink: 0 }} />
      ) : (
        avatarContent && (
          <Avatar sx={{ width: 64, height: 64, flexShrink: 0, bgcolor: avatarContent.bgcolor, color: "white" }}>
            {avatarContent.icon}
          </Avatar>
        )
      )}

      <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        {isLoading ? (
          <>
            <Skeleton variant="text" width={280} height={28} sx={{ lineHeight: "28px" }} />
            <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
              <Skeleton variant="rectangular" width={90} height={32} sx={{ borderRadius: 16 }} />
              <Skeleton variant="rectangular" width={85} height={32} sx={{ borderRadius: 16 }} />
              <Skeleton variant="rectangular" width={70} height={32} sx={{ borderRadius: 16 }} />
            </Stack>
            <Skeleton variant="text" width={180} height={24} sx={{ mt: 0.5 }} />
            <Skeleton variant="text" width={220} height={20} />
          </>
        ) : (
          <>
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
              {acctData?.firstName && acctData?.lastName
                ? `${acctData.firstName} ${acctData.lastName}`
                : acctData?.lastName || "Account Holder"}
            </Typography>

            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                <StatusTag type="accountStatus" isLoading={account.isLoading} typeId={acctStatusId} />
                <StatusTag type="customerType" isLoading={account.isLoading} typeId={customerTypeId} />
                {Boolean(acctData?.disconnected) && (
                  <Chip label="Disconnected" size="medium" color="error" variant="outlined" />
                )}
              </Stack>

              {acctData?.moveInDate && (
                <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>
                  Customer Since - {formatAccountCalendarDate(acctData.moveInDate)} -{" "}
                  {computeTenure(acctData.moveInDate, acctData.moveOutDate)}
                  {acctData.moveOutDate ? ` (Moved out: ${formatAccountCalendarDate(acctData.moveOutDate)})` : ""}
                </Typography>
              )}
            </Stack>

            <Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="h4"
                  sx={clickableTextSx}
                  onClick={() => acctData?.accountNum && copyToClipboard(acctData.accountNum, `Account number ${acctData.accountNum}`)}
                  title="Click to copy account number"
                >
                  Account #{acctData?.accountNum}
                </Typography>
                {hasOpenComplaints && (
                  <Chip label="Open Complaint" size="small" color="error" variant="outlined" />
                )}
              </Box>

              <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                {account.data.unit?.meters && account.data.unit.meters.length > 0 && (
                  <>
                    {account.data.unit.meters.map((meter) => (
                      <Box
                        key={meter.id ?? meter.uuid ?? meter.meterNum}
                        sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="h5"
                          sx={clickableTextSx}
                          onClick={() => {
                            const meterNum = meter?.meterNum;
                            if (meterNum) copyToClipboard(meterNum, `Meter number ${meterNum}`);
                          }}
                          title="Click to copy meter number"
                        >
                          Meter #{meter?.meterNum}
                        </Typography>
                        {meter?.physical !== undefined && (
                          <Chip
                            label={meter.physical ? "Physical Meter" : "Non-Metered"}
                            size="small"
                            color={meter.physical ? "default" : "info"}
                            sx={{ fontSize: "0.7rem", height: "22px" }}
                            title={meter.physical ? "Physical Meter" : "Non-Metered"}
                          />
                        )}
                      </Box>
                    ))}
                  </>
                )}
                {account.data.property?.isRemote !== undefined && (
                  <Chip
                    label={account.data.property.isRemote ? "Remote" : "Manual"}
                    size="small"
                    color={account.data.property.isRemote ? "warning" : "success"}
                    sx={{ fontSize: "0.7rem", height: "22px" }}
                    title={
                      account.data.property.isRemote
                        ? "Remote"
                        : "Manual"
                    }
                  />
                )}
              </Box>

              {acctData?.moveInDate && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Customer Since - {formatAccountCalendarDate(acctData.moveInDate)} -{" "}
                  {computeTenure(acctData.moveInDate, acctData.moveOutDate)}
                  {acctData.moveOutDate ? ` (Moved out: ${formatAccountCalendarDate(acctData.moveOutDate)})` : ""}
                </Typography>
              )}
            </Box>

            {account.data.unit && (
              <Typography
                variant="body1"
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": { opacity: 0.8 },
                }}
                onClick={() => {
                  if (account.data.unit?.uuid) {
                    window.open(
                      `/unit/${account.data.unit.uuid}/${account.data.unit.id}`,
                      "_blank"
                    );
                  }
                }}
                title="Click to view unit details"
              >
                {unitAddress}
              </Typography>
            )}

            {account.data.property?.name && (
              <Typography variant="body1" sx={{ mt: 1, fontWeight: 500, color: "text.primary" }}>
                {account.data.property.name}
              </Typography>
            )}

            {account.data.propertyManager?.name && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Property Manager: {account.data.propertyManager.name}
              </Typography>
            )}

            {(() => {
              const raw = account.data?.move?.moveOut;
              if (raw == null) return null;
              const moveOut = Array.isArray(raw) ? raw[0] : raw;
              if (!moveOut || moveOut.moveOutDate == null || moveOut.moveOutDate === "") return null;
              const processed = moveOut.processed;
              const isPending =
                processed === 0 || processed === false || processed === "0";
              if (!isPending) return null;
              return (
                <Box
                  sx={{
                    mt: 0.75,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                    rowGap: 0.5,
                  }}
                >
                  <Chip label="Pending Move Out" color="warning" size="small" sx={{ fontWeight: 500 }} />
                  <Typography variant="body2" color="text.secondary" component="span">
                    {formatAccountCalendarDate(moveOut.moveOutDate)}
                  </Typography>
                </Box>
              );
            })()}

            {moveInOutDates && (moveInOutDates.moveIn || moveInOutDates.moveOut) && (
              <Box sx={{ mt: 1.5 }}>
                {moveInOutDates.moveIn && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Move-in: {formatAccountCalendarDate(moveInOutDates.moveIn)}
                  </Typography>
                )}
                {moveInOutDates.moveOut && (
                  <Typography variant="body2" color="text.secondary">
                    Move-out: {formatAccountCalendarDate(moveInOutDates.moveOut)}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
});

export default AccountHeader;
